import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useForm } from "react-hook-form";
import PropTypes from "prop-types";

import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import WarningIcon from "@mui/icons-material/Warning";

import DialogPopup from "../../../../components/DialogPopup";
import Form from "../../../../components/Form/Form";
import { NewAccountSkeleton } from "../../../../components/Skeleton";
import LocalLoader from "../../../../components/Loaders/Local";

import accountImage from "../../../../img/selected-account.png";
import alAbocbImage from "../../../../img/selected-al-abocb.png";
import warningImage from "../../../../img/warning-big.png";
import { closeDialog } from "../../slices/dialog.slice";
import {
  useRegisterFormQuery,
  useSwitchAccountMutation,
} from "../../../../api/auth.api.slice";
import Styled from "./styles";
import { useCreateAccountMutation } from "../../../../api/tenants.api.slice";
import { updateFormFields } from "../../../../components/Form/context/formSlice";
import { fieldsVerified } from "../../../../components/Form/services/form.service";
import { showNotification } from "../../slices/notification.slice";
import { getLogin } from "../../../auth/slices/auth.slice";
import { notifyError } from "../../../../utils/helpers";
import useNavigationAway from "../../../../hooks/useNavigationAway";

function NewAccountPage(props) {
  const { type, openDialog } = props;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const initialState = {
    isAddAccountPage: true,
    isBusinessAccount: type === "Business",
    enableSSOPreFilledFields: "false",
    type,
  };
  const methods = useForm({ mode: "onBlur", defaultValues: initialState });
  const [skip, setSkip] = useState(true);
  const {
    data: registerFormConfig,
    isFetching,
    error: formError,
  } = useRegisterFormQuery("", {
    skip,
  });
  const showNavigatingAwayAlert = useNavigationAway();
  const [createAccount, { isLoading }] = useCreateAccountMutation();
  const [switchAccount, { isLoading: switchAccountLoading }] =
    useSwitchAccountMutation();

  const formConfig = { readOnly: false };
  const [showForm, setShowForm] = useState(false);

  const stateCode = process.env.REACT_APP_COMPLIA_INSTALL;

  const onDialogCancel = () => dispatch(closeDialog());

  useEffect(() => {
    if (registerFormConfig)
      dispatch(updateFormFields({ form: registerFormConfig }));
    return () => {
      dispatch(updateFormFields({ type: "reset" }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registerFormConfig]);

  useEffect(() => {
    if (formError) {
      notifyError(dispatch, showNotification, formError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formError]);

  const switchTenantHandler = async (id) => {
    try {
      await switchAccount(id).unwrap();
      dispatch(getLogin());
      dispatch(
        showNotification({
          type: "success",
          msg: "Account switched Successfully.",
          show: true,
        })
      );
      navigate("/licenses", { replace: true, state: "switchTenant" });
    } catch (err) {
      notifyError(dispatch, showNotification, err);
    }
  };

  const onSubmit = async (data) => {
    const errorMsg = fieldsVerified(data);
    if (!errorMsg) {
      let payload = {
        type,
        addCreatorToTenant: true,
        email: data.accountEmail,
        ...data,
        ...initialState,
      };
      delete payload.enableSSOPreFilledFields;
      if (type === "Business" && data.businessName) {
        payload = { ...payload, name: data.businessName };
      }
      try {
        if (!showNavigatingAwayAlert()) {
          onDialogCancel();
          return;
        }
        const res = await createAccount(payload).unwrap();
        if (res && res._id) {
          switchTenantHandler(res._id);
          onDialogCancel();
          const msg = "Account created successfully.";
          dispatch(showNotification({ type: "success", msg, show: true }));
        }
      } catch (err) {
        notifyError(dispatch, showNotification, err);
      }
    } else {
      dispatch(
        showNotification({ type: "warning", msg: errorMsg, show: true })
      );
    }
  };

  const onError = () => {
    const msg = `Please enter all required fields marked with a "*"`;
    dispatch(showNotification({ type: "warning", msg, show: true }));
  };

  const alertDialogContent = (
    <Styled>
      <Grid container sx={{ justifyContent: "center", alignItems: "center" }}>
        <Alert role="status" tabIndex="0" severity="info" elevation={2}>
          <Grid
            container
            spacing={1}
            sx={{ justifyContent: "center", alignItems: "center" }}
          >
            <Grid
              container
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              <Grid className="gridItem" size={{ sm: 12, md: 2 }}>
                <img
                  alt="Warning icon indicating an additional account is about to be created"
                  src={warningImage}
                  width="100"
                  height="100"
                />
              </Grid>
              <Grid size={{ sm: 12, md: 10 }}>
                <Typography variant="h6" className="warningText" tabIndex={0}>
                  <WarningIcon />
                  WARNING: You are about to create an additional account. If you
                  just registered, you do <u>NOT</u> need to create another
                  account. Please close this box and click the Create New
                  Application button to start your application. The account you
                  are currently working in is displayed in the left navigation:
                </Typography>
              </Grid>
            </Grid>
            <Grid
              container
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              <img
                alt="Current account displayed in the left navigation"
                src={stateCode !== "al-abocb" ? accountImage : alAbocbImage}
                width="250"
              />
            </Grid>

            <Grid
              container
              sx={{ justifyContent: "center", alignItems: "center" }}
            >
              <Button
                sx={{ mt: "0.625rem" }}
                variant="contained"
                onClick={() => {
                  setShowForm(true);
                  setSkip(false);
                }}
              >
                Continue to Account Creation
              </Button>
            </Grid>
          </Grid>
        </Alert>
      </Grid>
    </Styled>
  );

  const memoForm = useMemo(
    () =>
      registerFormConfig &&
      registerFormConfig.components &&
      registerFormConfig.components.length > 0 ? (
        <Grid
          container
          direction="row"
          sx={{ alignItems: "stretch", mb: "0.8rem" }}
        >
          <Grid size={{ xs: 12, sm: 12 }}>
            <Form
              formTemplate={registerFormConfig}
              methods={methods}
              formConfig={formConfig}
              applicationData={initialState}
              isDialogForm
            />
          </Grid>
        </Grid>
      ) : (
        <NewAccountSkeleton />
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [registerFormConfig]
  );
  const registerFormContent = (
    <Box sx={{ position: "relative" }}>
      {memoForm}
      <LocalLoader progress={isFetching || isLoading || switchAccountLoading} />
    </Box>
  );

  const buttonArray = [
    {
      id: 1,
      name: "CANCEL",
      icon: "",
      variant: "contained",
      onClickHandler: () => {
        onDialogCancel();
      },
      show: true,
    },
    {
      id: 2,
      name: "CREATE ACCOUNT",
      icon: "",
      variant: "contained",
      onClickHandler: methods.handleSubmit(onSubmit, onError),
      show: true,
    },
  ];
  return (
    <DialogPopup
      title={type ? `Create New Account - ${type}` : ""}
      showActionBtn={
        // this additional condition is for testing the behavior on edge cases
        showForm && type ? true : undefined
      }
      content={!showForm ? alertDialogContent : registerFormContent}
      openPopup={openDialog}
      onCancel={onDialogCancel}
      buttonArray={buttonArray}
      maxWidth="md"
      height={isFetching ? "70%" : undefined}
    />
  );
}

NewAccountPage.propTypes = {
  type: PropTypes.string.isRequired,
  openDialog: PropTypes.bool.isRequired,
};
export default NewAccountPage;
